import { useEffect, useMemo, useRef } from "react";
import type { Slot } from "../../api/types";
import type { BookingsMap, SlotData, Booking } from "../../hooks/bookingTypes";
import css from "./SlotPicker.module.css";
import { autoBlockNote, blockReasonOf } from "./blockInfo";

/**
 * Slot-status popover for the booking-modal time button. Per screen-03
 * эталон (`.cyber-pop.cyber-pop-time`): vertical list of all base slot
 * times for the current quest, each row shows the slot's status chip
 * (свободно / с предоплатой / без оплаты / свежий лид / блок / связанный
 * блок / сейчас выбран) plus a meta label.
 *
 * Block rows are disabled. Auto-block rows stay pickable (12.10, владелец: игру реально
 * провели в окне уборки) — их причина в meta, а сохранение спросит гейт («ВСЁ РАВНО …»).
 * The user's current slot row
 * gets the `.current` chip and `selected` style. Click a row → change
 * `time` and close.
 */

export type SlotStatus =
  | "free"
  | "booked-paid"
  | "booked-unpaid"
  | "fresh-lead"
  | "block"
  | "autoblock"
  | "current";

const STATUS_LABEL: Record<SlotStatus, string> = {
  free: "свободно",
  "booked-paid": "с предоплатой",
  "booked-unpaid": "без оплаты",
  "fresh-lead": "свежий лид",
  block: "блок",
  autoblock: "автоблок",
  current: "сейчас выбран",
};

const STATUS_CHIP: Record<SlotStatus, string | undefined> = {
  free: css.chipFree,
  "booked-paid": css.chipPaid,
  "booked-unpaid": css.chipUnpaid,
  "fresh-lead": css.chipLead,
  block: css.chipBlock,
  autoblock: css.chipAutoblock,
  current: css.chipCurrent,
};

export interface SlotPickerProps {
  /** Quest whose slots we're listing. */
  questId: string;
  /** All booked/auto-blocked slots for the current day, keyed by `${questId}|${time}`. */
  bookings: BookingsMap;
  /** Base slot times for the quest (from app.slots filtered by quest_id, sorted). */
  slotTimes: string[];
  /** The slot the modal is currently focused on. */
  currentTime: string;
  /** Editing-existing-booking? Then the current slot is showing the user's own row. */
  ownBookingId?: number;
  onPick: (time: string) => void;
  onClose: () => void;
}

function classify(
  data: SlotData | undefined,
  time: string,
  currentTime: string,
  ownBookingId: number | undefined,
): SlotStatus {
  if (time === currentTime) return "current";
  if (!data) return "free";
  if (data.type === "auto-block") return "autoblock";
  const b = data as Booking;
  /* Своя бронь: current уже отдан веткой time === currentTime выше. Сюда
     попадает ремап нестандартного времени на соседний слот (Б3) — колонка
     реально свободна, отдаём её как выбираемую. */
  if (ownBookingId && b.id === ownBookingId) return "free";
  if (b.type === "block") return "block";
  if (b.type === "lead") return "fresh-lead";
  return b.prepaid ? "booked-paid" : "booked-unpaid";
}

export function SlotPicker({
  questId,
  bookings,
  slotTimes,
  currentTime,
  ownBookingId,
  onPick,
  onClose,
}: SlotPickerProps) {
  const rootRef = useRef<HTMLDivElement>(null);

  const rows = useMemo(() => {
    return slotTimes.map((time) => {
      /* BookingsMap key is `${questId}_${time}` — see useBookings.ts. */
      const key = `${questId}_${time}`;
      const data = bookings[key];
      const status = classify(data, time, currentTime, ownBookingId);
      /* 28.09: у блока — зачем заблокировали */
      const note = status === "block" ? blockReasonOf(data as Booking) : data?.type === "auto-block" ? autoBlockNote(data) : "";
      return { time, status, note, disabled: status === "block" };
    });
  }, [slotTimes, questId, bookings, currentTime, ownBookingId]);

  /* Outside click + Esc close. */
  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <div ref={rootRef} className={css.pop} role="dialog" aria-label="Выбор слота">
      <div className={css.list}>
        {rows.map(({ time, status, note, disabled }) => (
          <button
            key={time}
            type="button"
            className={`${css.opt} ${status === "current" ? css.selected : ""} ${disabled ? css.disabled : ""}`}
            onClick={() => {
              if (disabled || status === "current") return;
              onPick(time);
              onClose();
            }}
            disabled={disabled}
            title={note || undefined}
          >
            <span className={`${css.chip} ${STATUS_CHIP[status]}`}>{time}</span>
            <span className={css.meta}>{STATUS_LABEL[status]}{note ? ` · ${note}` : ""}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
